/**
 * IssueReportDialog - Dialog for reporting P&P item issues
 *
 * Allows packers to report issues with pre-printed items:
 * - DAMAGED: Physical damage to item
 * - WRONG_ITEM: Different item than expected
 * - DEFECTIVE: Manufacturing defect
 * - MISSING: Item not found in bin
 *
 * CRITICAL: Large touch targets for radio buttons and buttons (glove-friendly).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PackingItemResult } from './ItemChecklist';

export type IssueType = 'DAMAGED' | 'WRONG_ITEM' | 'DEFECTIVE' | 'MISSING';

interface IssueReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: PackingItemResult | null;
  onSubmit: (issueType: IssueType, notes?: string) => void;
  isPending?: boolean;
}

const ISSUE_OPTIONS: { value: IssueType; labelKey: string; descKey: string }[] = [
  { value: 'DAMAGED', labelKey: 'packer.issueReport.damaged', descKey: 'packer.issueReport.damagedDesc' },
  { value: 'WRONG_ITEM', labelKey: 'packer.issueReport.wrongItem', descKey: 'packer.issueReport.wrongItemDesc' },
  { value: 'DEFECTIVE', labelKey: 'packer.issueReport.defective', descKey: 'packer.issueReport.defectiveDesc' },
  { value: 'MISSING', labelKey: 'packer.issueReport.missing', descKey: 'packer.issueReport.missingDesc' },
];

export function IssueReportDialog({
  open,
  onOpenChange,
  item,
  onSubmit,
  isPending = false,
}: IssueReportDialogProps) {
  const { t } = useTranslation('production');
  const [selectedIssue, setSelectedIssue] = useState<IssueType | null>(null);
  const [notes, setNotes] = useState('');

  // Reset state when dialog opens/closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSelectedIssue(null);
      setNotes('');
    }
    onOpenChange(newOpen);
  };

  const handleSubmit = () => {
    if (selectedIssue) {
      onSubmit(selectedIssue, notes.trim() || undefined);
    }
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="w-6 h-6" aria-hidden="true" />
            {t('packer.issueReport.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Item context */}
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="font-medium">{item.productName}</p>
            <div className="flex gap-2 mt-1">
              <Badge variant="outline" className="text-xs font-mono">
                {item.sku}
              </Badge>
              {item.size && (
                <Badge variant="secondary" className="text-xs">
                  {item.size}
                </Badge>
              )}
              {item.color && (
                <Badge variant="secondary" className="text-xs">
                  {item.color}
                </Badge>
              )}
            </div>
          </div>

          {/* Issue type selection - semantic fieldset with large touch targets */}
          <fieldset className="space-y-2">
            <legend className="text-base font-medium mb-2">{t('packer.issueReport.whatsWrong')}</legend>
            <div className="space-y-2" role="radiogroup" aria-required="true">
              {ISSUE_OPTIONS.map((issue) => (
                <button
                  key={issue.value}
                  type="button"
                  role="radio"
                  aria-checked={selectedIssue === issue.value}
                  onClick={() => setSelectedIssue(issue.value)}
                  disabled={isPending}
                  className={cn(
                    // CRITICAL: Large touch target for glove use
                    'w-full flex items-center gap-4 p-4 rounded-lg border-2 text-left transition-colors',
                    selectedIssue === issue.value
                      ? 'border-destructive bg-destructive/5'
                      : 'border-muted hover:border-muted-foreground/30',
                    isPending && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {/* Radio indicator - 24px for visibility */}
                  <div
                    aria-hidden="true"
                    className={cn(
                      'w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                      selectedIssue === issue.value
                        ? 'border-destructive'
                        : 'border-muted-foreground/50'
                    )}
                  >
                    {selectedIssue === issue.value && (
                      <div className="w-3 h-3 rounded-full bg-destructive" />
                    )}
                  </div>
                  <div className="flex-1">
                    <span className="font-medium text-base">{t(issue.labelKey)}</span>
                    <span className="block text-sm text-muted-foreground">
                      {t(issue.descKey)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </fieldset>

          {/* Notes textarea */}
          <div className="space-y-2">
            <Label htmlFor="issue-notes" className="text-base font-medium">
              {t('packer.issueReport.additionalNotes')}
            </Label>
            <Textarea
              id="issue-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('packer.issueReport.notesPlaceholder')}
              className="h-24 resize-none"
              disabled={isPending}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
            // CRITICAL: h-14 (56px) for buttons
            className="h-14"
          >
            {t('common:cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={!selectedIssue || isPending}
            // CRITICAL: h-14 (56px) for buttons
            className="h-14"
          >
            {isPending ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" aria-hidden="true" />
                {t('reporting')}
              </>
            ) : (
              t('packer.issueReport.submit')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
